import { Box, Divider, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useGetUserTaskItems, type TaskItemDto } from "src/http";
import { AutocompleteOption } from "src/components";
import { Autocomplete } from "../Autocomplete";

interface Props {
  userId: string | undefined;
  disabled?: boolean;
  onChange: (taskItem: TaskItemDto | null) => void;
}

const TaskItemPicker = (props: Props) => {
  const { data, isFetching } = useGetUserTaskItems(props.userId);
  const theme = useTheme();

  function sortFunction(a: TaskItemDto, b: TaskItemDto) {
    const aTeamName = a.team?.name ?? "No Team";
    const bTeamName = b.team?.name ?? "No Team";

    // Show "No Team" task items first in the list
    if (aTeamName === "No Team" && bTeamName !== "No Team") return -1;
    if (aTeamName !== "No Team" && bTeamName === "No Team") return 1;

    return aTeamName.localeCompare(bTeamName) || a.description.localeCompare(b.description);
  }

  return (
    <Autocomplete
      isLoading={isFetching}
      disabled={!props.userId || props.disabled || isFetching}
      options={data ? [...data.taskItems].sort(sortFunction) : []}
      getOptionLabel={(option) => option.description}
      renderOption={(props, option) => (
        <AutocompleteOption {...props} key={option.guid}>
          <Typography>{option.description}</Typography>
        </AutocompleteOption>
      )}
      isOptionEqualToValue={(option, value) => option.guid === value.guid}
      groupBy={(option) => option.team?.name ?? "No Team"}
      renderGroup={(group) => (
        <Box key={group.key} sx={{ pb: 0.25 }}>
          <Stack
            spacing={0.2}
            sx={{
              px: 0.5,
              pt: 0.75,
              position: "sticky",
              top: 0,
              backgroundColor: theme.palette.common.white,
              zIndex: 2,
            }}
          >
            <Typography variant="caption">{group.group.toLocaleUpperCase()}</Typography>
            <Divider />
          </Stack>
          {group.children}
        </Box>
      )}
      renderInput={(params) => <TextField {...params} placeholder="Select a task item..." />}
      onChange={(_, value) => props.onChange(value)}
    />
  );
};

export { TaskItemPicker };
